// state/scoped-chat.ts — founder chat-scoping contract (Item B, 2026-06-11).
//
// The founder's law for the in-lesson / in-sub-argomento Prof chat (verbatim):
//
//   "the chat is available for that session and on that topic only, meaning that
//    as long as I am in the same lesson, it keeps going maintaining the same
//    session, once I change lesson or sub-argomento by switching to another
//    lesson or clicking to go to another part of the app, then the session/chat
//    is stored in cronologia, but if I go back to the same sub-argomento or
//    lesson, a new fresh chat/session is triggered."
//
// This store mints a per-VISIT nonce for a scoped topic so the thread id is
//   `<surface>:<topicId>:<visitNonce>`
// and:
//   - the SAME visit keeps the same threadId (and therefore the same BE session)
//     — "as long as I am in the same lesson, it keeps going";
//   - LEAVING the topic (navigating to a different topic/lesson/section, or any
//     non-scoped surface) and RETURNING mints a NEW nonce → a NEW threadId → a
//     FRESH chat/session — "a new fresh chat/session is triggered".
//
// "Topic key" = `<surface>:<topicId>` WITHOUT the nonce — the stable identity of
// the lesson/sub-argomento. Navigating WITHIN the same lesson (e.g. paging
// chapters of a guided lesson, where the pathname-topic does not change) keeps
// the same topic key → same nonce → same thread. Switching to another
// lesson/sub-argomento changes the topic key → new nonce on re-enter.
//
// GlobalChatSheet calls `enterScopedTopic(topicKey)` whenever a scoped surface is
// active, and `leaveScopedTopic()` when the active surface is NOT scoped (a hub
// page or the main /prof). Both are idempotent-friendly: calling enter with the
// SAME topic key as the currently-active one is a no-op (keeps the nonce).

import { create } from 'zustand';

interface ScopedChatState {
  /** The topic key currently being visited (`<surface>:<topicId>`), or null when
   *  no scoped surface is active. */
  activeTopicKey: string | null;
  /** The current visit nonce for `activeTopicKey`. Changes on every fresh ENTER
   *  of a topic that differs from the previously-active one. */
  activeNonce: string | null;

  /**
   * Enter a scoped topic. If it differs from the currently-active topic key, a
   * FRESH nonce is minted (fresh-on-return / switch). If it is the SAME topic
   * key already active, the nonce is kept (same-visit continuity). Returns the
   * resolved thread id `<topicKey>:<nonce>`.
   */
  enterScopedTopic: (topicKey: string) => string;

  /** Leave any scoped topic (the active surface is a hub page or the main Prof).
   *  Clears the active topic so the NEXT enter of any topic mints a fresh nonce
   *  ("clicking to go to another part of the app" then returning = fresh). */
  leaveScopedTopic: () => void;
}

/** Mint a short, unique visit nonce. crypto.randomUUID is available in the app
 *  runtime + jsdom; the slice keeps the thread id compact. */
function mintNonce(): string {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    // Defensive fallback for any environment without crypto.randomUUID.
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  }
}

export const useScopedChat = create<ScopedChatState>((set, get) => ({
  activeTopicKey: null,
  activeNonce: null,

  enterScopedTopic: (topicKey) => {
    const s = get();
    if (s.activeTopicKey === topicKey && s.activeNonce) {
      // Same visit — keep going.
      return `${topicKey}:${s.activeNonce}`;
    }
    // Fresh enter (different topic, or returning after leaving) — new nonce.
    const nonce = mintNonce();
    set({ activeTopicKey: topicKey, activeNonce: nonce });
    return `${topicKey}:${nonce}`;
  },

  leaveScopedTopic: () => {
    if (get().activeTopicKey === null && get().activeNonce === null) return;
    set({ activeTopicKey: null, activeNonce: null });
  },
}));

/**
 * Pure helper: derive the stable `<surface>:<topicId>` topic key for a scoped
 * surface, given its flow key and the live pathname. Returns null for surfaces
 * that are NOT scoped (hubs / main Prof). The topicId comes from the LAST path
 * segment for the lesson/principle surfaces and the argomento segment for the
 * theory/domande surfaces — the same source `useScreenFlow`/`GlobalChatSheet`
 * already parse, so it is always resolvable from the URL.
 */
export function scopedTopicKeyFor(flowKey: string, pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1] ?? '';
  switch (flowKey) {
    case 'lesson-fast':
    case 'lesson-deep':
    case 'lesson-guidata':
    case 'punti-chiave':
      // .../<id> — micro-topic id or argomento id.
      return last ? `lesson:${last}` : null;
    case 'appendice-detail':
      // /appendice/:argomentoId/:principleId — scope by the principle id.
      return last ? `appendice-detail:${last}` : null;
    case 'appendice-theory':
      // /appendice/:argomentoId/teoria — scope by the argomento id.
      return segments.length >= 2 ? `appendice-theory:${segments[1]}` : null;
    case 'appendice-domande':
      // /appendice/:argomentoId/domande — scope by the argomento id.
      return segments.length >= 2 ? `appendice-domande:${segments[1]}` : null;
    case 'appendice-signs':
      // /appendice/segnali OR /appendice/segnali/:signId — scope by the sign id
      // when present, else the index itself.
      return `appendice-signs:${last === 'segnali' ? 'index' : last}`;
    default:
      return null;
  }
}
